// Markdown prose renderer with inline citation chips (spec 16 §5).
//
// react-markdown does the parsing. We pre-process the text to swap
// `[N]` citation markers for a sentinel HTML-ish span the markdown
// parser passes through unchanged, then re-hydrate them as React
// citation chips via the custom `span` component override.
//
// Why pre-process: we don't enable rehype-raw (raw HTML stays
// disabled for safety), so we route citations through plain text
// + a custom component override keyed by className. Standard
// react-markdown pattern.

import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ProseProps {
	text: string;
	/** Refs known to be present in the sources strip; chips matching
	 *  these get the active style. Others render dimmer. */
	knownRefs?: Set<number>;
	/** Called when a chip is clicked. The page typically scrolls /
	 *  flashes the source card with matching ref. */
	onCiteClick?: (ref: number) => void;
}

// Marker sentinel: render `[N]` as `[[CITE:N]]` in pre-processing,
// then a remark plugin (below) swaps it for an inline node. We avoid
// HTML pass-through entirely.

const CITE_REGEX = /\[(\d{1,2})\]/g;

export function Prose({ text, knownRefs, onCiteClick }: ProseProps) {
	// Replace [N] with `{{cite:N}}` placeholders that react-markdown
	// will pass through as plain text. We then post-process the
	// rendered children to swap placeholders for chip components.
	//
	// More robust than rehype-raw: zero HTML attack surface.
	const transformed = useMemo(() => text.replace(CITE_REGEX, "{{cite:$1}}"), [text]);

	return (
		<div className="prose">
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					// Intercept every text fragment and swap citation
					// placeholders for chip elements.
					p: ({ children }) => <p>{renderWithChips(children, knownRefs, onCiteClick)}</p>,
					li: ({ children }) => <li>{renderWithChips(children, knownRefs, onCiteClick)}</li>,
					em: ({ children }) => <em>{renderWithChips(children, knownRefs, onCiteClick)}</em>,
					strong: ({ children }) => <strong>{renderWithChips(children, knownRefs, onCiteClick)}</strong>,
					a: ({ children, href }) => (
						<a href={href} target="_blank" rel="noreferrer noopener">
							{renderWithChips(children, knownRefs, onCiteClick)}
						</a>
					),
					// Headings: h1 reserved (we never emit it). h2/h3 only.
					h1: ({ children }) => <h2>{renderWithChips(children, knownRefs, onCiteClick)}</h2>,
				}}
			>
				{transformed}
			</ReactMarkdown>
		</div>
	);
}

const PLACEHOLDER_REGEX = /\{\{cite:(\d{1,2})\}\}/g;

function renderWithChips(
	children: React.ReactNode,
	knownRefs?: Set<number>,
	onCiteClick?: (ref: number) => void,
): React.ReactNode {
	// Walk the children; for any string, split on the placeholder and
	// interleave Chip components. Anything that isn't a string is
	// passed through as-is (react-markdown can hand us nested arrays
	// and elements; only strings carry the placeholder).
	const out: React.ReactNode[] = [];
	let key = 0;

	const visit = (node: React.ReactNode) => {
		if (typeof node === "string") {
			const parts = node.split(PLACEHOLDER_REGEX);
			// parts alternates: text, ref, text, ref, ...
			for (let i = 0; i < parts.length; i++) {
				const piece = parts[i];
				if (i % 2 === 0) {
					if (piece) out.push(<span key={key++}>{piece}</span>);
				} else {
					const n = Number(piece);
					if (!Number.isFinite(n)) continue;
					const active = knownRefs ? knownRefs.has(n) : true;
					out.push(
						<a
							key={key++}
							href={`#source-${n}`}
							className={`citation-chip ${active ? "citation-chip-active" : ""}`}
							onClick={(e) => {
								if (onCiteClick) {
									e.preventDefault();
									onCiteClick(n);
								}
							}}
						>
							{n}
						</a>,
					);
				}
			}
			return;
		}
		if (Array.isArray(node)) {
			node.forEach(visit);
			return;
		}
		// Non-string node — just push it through; nested visits will
		// handle deeper strings.
		out.push(<span key={key++}>{node as React.ReactNode}</span>);
	};

	visit(children);
	return out;
}
